import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  LogOut, Plus, QrCode, Trash2, Images, X, Copy, Download,
  ExternalLink, Loader2, ChefHat, Mail, Pencil, Check, EyeOff,
} from "lucide-react";
import { api, clearToken, getToken, prettyDate, monthYear, formatApiError } from "../lib/api";
import { Stars } from "../components/Stars";

const DISHES = [
  { key: "soup", label: "Clear Chicken Soup" },
  { key: "boeuf", label: "Boeuf Salad" },
  { key: "paprikash", label: "Chicken Paprikash" },
];

const keepsakeUrl = (id) => `${window.location.origin}/keepsake/${id}`;

function CreateForm({ onCreated }) {
  const [guestName, setGuestName] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [note, setNote] = useState("");
  const [dishes, setDishes] = useState(DISHES.map((d) => d.key));
  const [busy, setBusy] = useState(false);

  const toggle = (k) =>
    setDishes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/experiences", {
        guest_name: guestName,
        visit_date: visitDate || null,
        note: note || null,
        dishes,
      });
      setGuestName(""); setVisitDate(""); setNote(""); setDishes(DISHES.map((d) => d.key));
      toast.success("Keepsake created");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[color:var(--rule)] p-6 sm:p-8 bg-[color:var(--paper)]" data-testid="create-keepsake-form">
      <p className="eyebrow text-[color:var(--moss)]">New guest keepsake</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-5">
        <div>
          <label className="letter-label" htmlFor="guest">Guest name</label>
          <input id="guest" className="letter-input" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="e.g. The Popescu family" data-testid="create-guest-name" required />
        </div>
        <div>
          <label className="letter-label" htmlFor="date">Visit date</label>
          <input id="date" type="date" className="letter-input" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="create-visit-date" />
        </div>
      </div>
      <div className="mt-6">
        <label className="letter-label" htmlFor="note">A personal note (optional)</label>
        <textarea id="note" className="letter-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a few warm words they'll read on their page…" data-testid="create-note" />
      </div>
      <div className="mt-6">
        <p className="letter-label">Dishes they had</p>
        <div className="flex flex-wrap gap-3 mt-1">
          {DISHES.map((d) => (
            <button
              type="button"
              key={d.key}
              onClick={() => toggle(d.key)}
              className={`px-4 py-2 border text-sm transition-colors ${
                dishes.includes(d.key)
                  ? "border-[color:var(--moss)] bg-[color:var(--moss)] text-[color:var(--paper)]"
                  : "border-[color:var(--rule)] text-[color:var(--ink-2)]"
              }`}
              data-testid={`create-dish-${d.key}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <button type="submit" className="btn-primary mt-7 inline-flex gap-2" disabled={busy} data-testid="create-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Create keepsake
      </button>
    </form>
  );
}

function QrModal({ experience, onClose }) {
  const ref = useRef(null);
  const url = keepsakeUrl(experience.id);

  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `keepsake-${experience.guest_name.replace(/\s+/g, "-").toLowerCase()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[color:var(--ink)]/70 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-modal">
      <div className="bg-[color:var(--paper)] border border-[color:var(--rule)] p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <button className="float-right text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" onClick={onClose} data-testid="qr-close"><X className="w-5 h-5" /></button>
        <p className="eyebrow text-[color:var(--moss)]">Scan at the table</p>
        <h3 className="font-display text-2xl mt-2 mb-5">{experience.guest_name}</h3>
        <div ref={ref} className="inline-block bg-white p-4 photo-shadow">
          <QRCodeCanvas value={url} size={220} level="M" fgColor="#2C2825" bgColor="#ffffff" includeMargin data-testid="qr-canvas" />
        </div>
        <p className="font-body text-xs text-[color:var(--ink-3)] mt-4 break-all">{url}</p>
        <div className="flex gap-3 mt-6">
          <button className="btn-ghost flex-1" onClick={copy} data-testid="qr-copy"><Copy className="w-4 h-4" /> Copy link</button>
          <button className="btn-primary flex-1" onClick={download} data-testid="qr-download"><Download className="w-4 h-4" /> Download</button>
        </div>
      </div>
    </div>
  );
}

function ManageModal({ experienceId, onClose, onChanged }) {
  const [exp, setExp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/experiences/${experienceId}`);
    setExp(data);
  }, [experienceId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        await api.post(`/admin/experiences/${experienceId}/photos`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added`);
    if (fileRef.current) fileRef.current.value = "";
    setUploading(false);
    await load();
    onChanged();
  };

  const removePhoto = async (photoId) => {
    await api.delete(`/admin/photos/${photoId}`);
    toast.success("Photo removed");
    await load();
    onChanged();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[color:var(--ink)]/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="manage-modal">
      <div className="bg-[color:var(--paper)] border border-[color:var(--rule)] p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-[color:var(--moss)]">Manage photos</p>
            <h3 className="font-display text-3xl mt-1">{exp?.guest_name || "…"}</h3>
            {exp?.visit_date && <p className="font-hand text-xl text-[color:var(--moss)]">{prettyDate(exp.visit_date)}</p>}
          </div>
          <button className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" onClick={onClose} data-testid="manage-close"><X className="w-6 h-6" /></button>
        </div>

        <div className="mt-6">
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="photo-input" />
          <button className="btn-primary inline-flex gap-2" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-photos-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Images className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Add photos"}
          </button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
          {(exp?.photos || []).map((p) => (
            <div key={p.id} className="relative aspect-square bg-[color:var(--paper-2)] group" data-testid={`manage-photo-${p.id}`}>
              <img src={`${api.defaults.baseURL}/photos/${p.id}`} alt={p.original_filename || ""} className="photo" />
              <button
                onClick={() => removePhoto(p.id)}
                className="absolute top-1 right-1 bg-[color:var(--ink)]/80 text-[color:var(--paper)] p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#a13a2a]"
                data-testid={`delete-photo-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {exp && exp.photos?.length === 0 && (
            <p className="font-body text-[color:var(--ink-3)] col-span-full italic">No photos yet — add some from the afternoon.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function EditModal({ experience, onClose, onSaved }) {
  const [guestName, setGuestName] = useState(experience.guest_name || "");
  const [visitDate, setVisitDate] = useState(experience.visit_date || "");
  const [note, setNote] = useState(experience.note || "");
  const [dishes, setDishes] = useState(experience.dishes || DISHES.map((d) => d.key));
  const [busy, setBusy] = useState(false);

  const toggle = (k) =>
    setDishes((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/experiences/${experience.id}`, {
        guest_name: guestName,
        visit_date: visitDate || null,
        note: note || null,
        dishes,
      });
      toast.success("Keepsake updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[color:var(--ink)]/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="edit-modal">
      <form className="bg-[color:var(--paper)] border border-[color:var(--rule)] p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-[color:var(--moss)]">Edit keepsake</p>
            <h3 className="font-display text-3xl mt-1">Guest details</h3>
          </div>
          <button type="button" className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]" onClick={onClose} data-testid="edit-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="letter-label" htmlFor="edit-guest">Guest name</label>
            <input id="edit-guest" className="letter-input" value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="edit-guest-name" required />
          </div>
          <div>
            <label className="letter-label" htmlFor="edit-date">Visit date</label>
            <input id="edit-date" type="date" className="letter-input" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="edit-visit-date" />
          </div>
        </div>
        <div className="mt-6">
          <label className="letter-label" htmlFor="edit-note">Personal note</label>
          <textarea id="edit-note" className="letter-input" value={note} onChange={(e) => setNote(e.target.value)} data-testid="edit-note" />
        </div>
        <div className="mt-6">
          <p className="letter-label">Dishes they had</p>
          <div className="flex flex-wrap gap-3 mt-1">
            {DISHES.map((d) => (
              <button type="button" key={d.key} onClick={() => toggle(d.key)}
                className={`px-4 py-2 border text-sm transition-colors ${dishes.includes(d.key) ? "border-[color:var(--moss)] bg-[color:var(--moss)] text-[color:var(--paper)]" : "border-[color:var(--rule)] text-[color:var(--ink-2)]"}`}
                data-testid={`edit-dish-${d.key}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>
        <button type="submit" className="btn-primary mt-7 inline-flex gap-2" disabled={busy} data-testid="edit-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
          Save changes
        </button>
      </form>
    </div>
  );
}

function ExperienceCard({ exp, onQr, onManage, onEdit, onDelete }) {
  return (
    <div className="border border-[color:var(--rule)] p-6 bg-[color:var(--paper)] flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`keepsake-row-${exp.id}`}>
      <div>
        <h3 className="font-display text-2xl leading-tight">{exp.guest_name}</h3>
        <p className="font-body text-sm text-[color:var(--ink-2)] mt-1">
          {exp.visit_date ? prettyDate(exp.visit_date) : "No date"} · {exp.photo_count} photo{exp.photo_count === 1 ? "" : "s"} · {(exp.dishes || []).length} recipe{(exp.dishes || []).length === 1 ? "" : "s"}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={keepsakeUrl(exp.id)} target="_blank" rel="noreferrer" className="btn-ghost !px-4 !py-2 !min-h-0" data-testid={`view-keepsake-${exp.id}`}><ExternalLink className="w-4 h-4" /> View</a>
        <button className="btn-ghost !px-4 !py-2 !min-h-0" onClick={() => onManage(exp.id)} data-testid={`manage-keepsake-${exp.id}`}><Images className="w-4 h-4" /> Photos</button>
        <button className="btn-ghost !px-4 !py-2 !min-h-0" onClick={() => onEdit(exp)} data-testid={`edit-keepsake-${exp.id}`}><Pencil className="w-4 h-4" /> Edit</button>
        <button className="btn-ghost !px-4 !py-2 !min-h-0" onClick={() => onQr(exp)} data-testid={`qr-keepsake-${exp.id}`}><QrCode className="w-4 h-4" /> QR</button>
        <button className="btn-ghost !px-4 !py-2 !min-h-0 !border-[#a13a2a] !text-[#a13a2a] hover:!bg-[#a13a2a] hover:!text-white" onClick={() => onDelete(exp)} data-testid={`delete-keepsake-${exp.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("keepsakes");
  const [experiences, setExperiences] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrExp, setQrExp] = useState(null);
  const [manageId, setManageId] = useState(null);
  const [editExp, setEditExp] = useState(null);

  const logout = useCallback(() => {
    clearToken();
    navigate("/admin/login");
  }, [navigate]);

  const loadExperiences = useCallback(async () => {
    const { data } = await api.get("/admin/experiences");
    setExperiences(data);
  }, []);

  const loadInquiries = useCallback(async () => {
    const { data } = await api.get("/admin/inquiries");
    setInquiries(data);
  }, []);

  const loadReviews = useCallback(async () => {
    const { data } = await api.get("/admin/reviews");
    setReviews(data);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        await api.get("/auth/me");
        await Promise.all([loadExperiences(), loadInquiries(), loadReviews()]);
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteExperience = async (exp) => {
    if (!window.confirm(`Delete the keepsake for ${exp.guest_name}? This cannot be undone.`)) return;
    await api.delete(`/admin/experiences/${exp.id}`);
    toast.success("Keepsake deleted");
    loadExperiences();
  };

  const setReviewStatus = async (id, status) => {
    await api.put(`/admin/reviews/${id}`, { status });
    toast.success(status === "published" ? "Published to the site" : "Hidden from the site");
    loadReviews();
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this note permanently?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Note deleted");
    loadReviews();
  };

  const pendingCount = reviews.filter((r) => r.status === "pending").length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--paper)]">
        <Loader2 className="w-8 h-8 animate-spin text-[color:var(--moss)]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--paper)]" data-testid="admin-dashboard">
      <header className="border-b border-[color:var(--rule)] px-6 sm:px-10 py-6 flex items-center justify-between sticky top-0 bg-[color:var(--paper)] z-40">
        <div className="flex items-center gap-3">
          <ChefHat className="w-6 h-6 text-[color:var(--gold)]" />
          <div>
            <p className="font-display text-2xl leading-none">theunfinishedlunch</p>
            <p className="eyebrow mt-1">Host studio</p>
          </div>
        </div>
        <button className="btn-ghost !px-4 !py-2 !min-h-0" onClick={logout} data-testid="logout-button"><LogOut className="w-4 h-4" /> Sign out</button>
      </header>

      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex gap-2 mb-8 border-b border-[color:var(--rule)]">
          <button
            className={`pb-3 px-2 eyebrow border-b-2 -mb-[1px] transition-colors ${tab === "keepsakes" ? "border-[color:var(--moss)] text-[color:var(--moss)]" : "border-transparent text-[color:var(--ink-3)]"}`}
            onClick={() => setTab("keepsakes")}
            data-testid="tab-keepsakes"
          >
            Keepsakes ({experiences.length})
          </button>
          <button
            className={`pb-3 px-2 eyebrow border-b-2 -mb-[1px] transition-colors ${tab === "inquiries" ? "border-[color:var(--moss)] text-[color:var(--moss)]" : "border-transparent text-[color:var(--ink-3)]"}`}
            onClick={() => setTab("inquiries")}
            data-testid="tab-inquiries"
          >
            Inquiries ({inquiries.length})
          </button>
          <button
            className={`pb-3 px-2 eyebrow border-b-2 -mb-[1px] transition-colors relative ${tab === "reviews" ? "border-[color:var(--moss)] text-[color:var(--moss)]" : "border-transparent text-[color:var(--ink-3)]"}`}
            onClick={() => setTab("reviews")}
            data-testid="tab-reviews"
          >
            Notes ({reviews.length})
            {pendingCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] leading-none rounded-full bg-[color:var(--gold)] text-white" data-testid="pending-reviews-badge">
                {pendingCount}
              </span>
            )}
          </button>
        </div>

        {tab === "keepsakes" && (
          <div className="space-y-6">
            <CreateForm onCreated={loadExperiences} />
            <div className="space-y-4">
              {experiences.length === 0 && (
                <p className="font-body text-[color:var(--ink-3)] italic">No keepsakes yet. Create one above after a lunch.</p>
              )}
              {experiences.map((exp) => (
                <ExperienceCard key={exp.id} exp={exp} onQr={setQrExp} onManage={setManageId} onEdit={setEditExp} onDelete={deleteExperience} />
              ))}
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div className="space-y-4" data-testid="inquiries-list">
            {inquiries.length === 0 && (
              <p className="font-body text-[color:var(--ink-3)] italic">No inquiries yet.</p>
            )}
            {inquiries.map((q) => (
              <div key={q.id} className="border border-[color:var(--rule)] p-6 bg-[color:var(--paper)]" data-testid={`inquiry-${q.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl">{q.name}</h3>
                  <span className="font-body text-xs text-[color:var(--ink-3)]">{prettyDate(q.created_at)}</span>
                </div>
                <a href={`mailto:${q.email}`} className="inline-flex items-center gap-2 text-[color:var(--moss)] text-sm mt-1"><Mail className="w-3.5 h-3.5" /> {q.email}</a>
                <p className="font-body text-sm text-[color:var(--ink-2)] mt-3">
                  {q.preferred_date && <span className="mr-4"><strong>When:</strong> {q.preferred_date}</span>}
                  {q.group_size && <span className="mr-4"><strong>Group:</strong> {q.group_size}</span>}
                  {q.dietary_notes && <span><strong>Dietary:</strong> {q.dietary_notes}</span>}
                </p>
                {q.message && <p className="font-body text-[color:var(--ink)] mt-3 italic leading-relaxed">"{q.message}"</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-4" data-testid="reviews-list">
            <p className="font-body text-sm text-[color:var(--ink-2)]">
              Notes from guests. Tap <strong>Publish</strong> to show one in the "Kind words" section on your homepage.
            </p>
            {reviews.length === 0 && (
              <p className="font-body text-[color:var(--ink-3)] italic">No guest notes yet.</p>
            )}
            {reviews.map((r) => (
              <div key={r.id} className="border border-[color:var(--rule)] p-6 bg-[color:var(--paper)]" data-testid={`review-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-display text-xl">{r.author_name}</h3>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${r.status === "published" ? "bg-[color:var(--moss)] text-white" : "bg-[color:var(--paper-2)] text-[color:var(--ink-2)] border border-[color:var(--rule)]"}`} data-testid={`review-status-${r.id}`}>
                        {r.status === "published" ? "Published" : "Pending"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <Stars value={r.rating} size={14} />
                      {r.visit_date && <span className="eyebrow">{monthYear(r.visit_date)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {r.status === "pending" ? (
                      <button className="btn-primary !px-4 !py-2 !min-h-0 inline-flex gap-2" onClick={() => setReviewStatus(r.id, "published")} data-testid={`publish-review-${r.id}`}><Check className="w-4 h-4" /> Publish</button>
                    ) : (
                      <button className="btn-ghost !px-4 !py-2 !min-h-0 inline-flex gap-2" onClick={() => setReviewStatus(r.id, "pending")} data-testid={`unpublish-review-${r.id}`}><EyeOff className="w-4 h-4" /> Hide</button>
                    )}
                    <button className="btn-ghost !px-4 !py-2 !min-h-0 !border-[#a13a2a] !text-[#a13a2a] hover:!bg-[#a13a2a] hover:!text-white" onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="font-body text-[color:var(--ink)] mt-4 italic leading-relaxed">"{r.note}"</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {qrExp && <QrModal experience={qrExp} onClose={() => setQrExp(null)} />}
      {manageId && <ManageModal experienceId={manageId} onClose={() => setManageId(null)} onChanged={loadExperiences} />}
      {editExp && <EditModal experience={editExp} onClose={() => setEditExp(null)} onSaved={loadExperiences} />}
    </div>
  );
}
